import {Dispatch, FC, Fragment, SetStateAction, useEffect, useState} from 'react'
import {fixZero, isNotEmpty, numberWithCommas} from '../../../../../../../_metronic/helpers'
import {useListView} from '../../core/ListViewProvider'
import {InvoiceRowForm} from '../../core/_models'
import {InvoiceDetailsGrid} from '../components/InvoicePresentation'
import {calculateRemainingInvoiceQuantity} from '../../core/invoiceQuantity'

type props = {
  ind: number
  rw: InvoiceRowForm
  setEditInvoiceItem: Dispatch<SetStateAction<InvoiceRowForm | null | undefined>>
  deleteInvoiceItem: (rw: InvoiceRowForm) => void
  returnQty: {
    returnQty: InvoiceRowForm[]
    setReturnQty: Dispatch<SetStateAction<InvoiceRowForm[]>>
  }
  invoiceTemplate: number
  invoiceDate: string
  readOnly?: boolean
  showDiscount: boolean
  showTax: boolean
  columnCount: number
  isEditing?: boolean
}

const Row: FC<props> = ({
  ind,
  rw,
  setEditInvoiceItem,
  deleteInvoiceItem,
  returnQty,
  invoiceTemplate,
  invoiceDate,
  readOnly = false,
  showDiscount,
  showTax,
  columnCount,
  isEditing = false,
}) => {
  const {variableD} = useListView()
  const [expanded, setExpanded] = useState(false)
  const [value, setValue] = useState<number>(
    returnQty.returnQty.find((x) => x.id == rw.id)?.qty ?? 0
  )
  const finded = returnQty && returnQty.returnQty.find((x) => x.id == rw.id)
  const [discount, setDiscount] = useState<string>(
    ((variableD == 4 && finded != undefined ? finded.discount : rw.discount) || 0).toString()
  )
  const getPrice: () => number = () => {
    // مبلغ واحد می‌تواند اعشاری باشد؛ مثل فرم قلم و Backend فقط حاصل‌ضرب گرد می‌شود، نه مبلغ واحد
    return fixZero((rw.amount ?? 0) * ((rw.qty ?? 0) - value))
  }
  const getAdPrice: () => number = () => {
    let price = getPrice() - fixZero(Number(discount || '0')) - (rw.totalDiscount || 0)
    if (invoiceTemplate == 3) {
      price +=
        parseFloat((rw.wage || 0).toString()) +
        parseFloat((rw.profit || 0).toString()) +
        parseFloat((rw.commission || 0).toString())
    }
    return price
  }

  const getVatPrice: () => number = () => {
    let vat = getAdPrice() * ((rw.vatRate || 0) / 100)
    if (invoiceTemplate == 3) {
      const _sum =
        parseFloat((rw.wage || 0).toString()) +
        parseFloat((rw.profit || 0).toString()) +
        parseFloat((rw.commission || 0).toString())
      if (invoiceDate.startsWith('1404/') || invoiceDate.startsWith('1405/')) {
        vat += _sum * 0.1
      } else {
        vat += Math.floor(parseFloat((_sum * 0.09).toString()))
      }
    }
    return fixZero(vat)
  }
  const getTaxPrice: () => number = () => {
    return fixZero(getAdPrice() * (Number(rw.taxRate || 0) / 100))
  }
  const getGovTaxPrice: () => number = () => {
    return fixZero(getAdPrice() * (Number(rw.govTaxRate || 0) / 100))
  }
  const getNetPrice: () => number = () => {
    return getAdPrice() + getVatPrice() + getTaxPrice() + getGovTaxPrice()
  }

  useEffect(() => {
    let _discount = fixZero(Number(discount || '0'))
    try {
      if (_discount > (rw.discount || 0)) {
        _discount = rw.discount || 0
      }
      if (_discount < 0) {
        _discount = 0
      }
    } catch (error) {
      _discount = getPrice() - (rw.totalDiscount || 0)
    }
    if (getNetPrice() < 0) {
      _discount = getPrice() - (rw.totalDiscount || 0)
    }
    setDiscount(_discount.toString())
    change()
  }, [discount])

  useEffect(() => {
    if (value > (rw.qty ?? 0)) {
      setValue(rw.qty ?? 0)
    }
    if (value < 0) {
      setValue(0)
    }
    if (getNetPrice() < 0) {
      setValue(value - 1)
    }
    change()
  }, [value])

  const change = () => {
    const newValue: InvoiceRowForm = {
      qty: value,
      discount: Number(discount),
      id: rw.id,
      productId: rw.productId,
      product: rw.product,
      amount: rw.amount,
      totalDiscount: rw.totalDiscount,
      description: rw.description,
      price: getPrice(),
      adPrice: getAdPrice(),
      vatRate: rw.vatRate,
      vatPrice: getVatPrice(),
      taxName: rw.taxName,
      taxRate: rw.taxRate,
      taxPrice: getTaxPrice(),
      govTaxName: rw.govTaxName,
      govTaxRate: rw.govTaxRate,
      govTaxPrice: getGovTaxPrice(),
      netPrice: getNetPrice(),
      unit: rw.unit,
    }
    const ind = returnQty.returnQty.findIndex((x) => x.id == rw.id)
    if (ind >= 0) {
      returnQty.returnQty.splice(ind, 1)
    }
    returnQty.setReturnQty([newValue, ...returnQty.returnQty])
  }

  const title =
    rw.description && isNotEmpty(rw.description)
      ? rw.description
      : rw.product?.nm || 'کالا یا خدمت بدون شرح'
  const otherTaxPrice = getTaxPrice() + getGovTaxPrice()
  const productCode = rw.product?.stin?.cd || rw.product?.sti || rw.product?.cd
  const remainingQuantity = calculateRemainingInvoiceQuantity(rw.qty, value)

  return (
    <Fragment>
      <tr
        className={`mp-invoice-data-row${expanded ? ' is-expanded' : ''}${
          isEditing ? ' is-editing' : ''
        }${readOnly ? ' is-readonly' : ''}`}
      >
        <td className='mp-invoice-cell--index text-center' data-label='ردیف'>
          <span className='mp-invoice-row-number'>{ind + 1}</span>
        </td>
        <td className='mp-invoice-cell--item text-start' data-label='کالا یا خدمت'>
          <strong className='mp-invoice-item-title' title={title}>
            {title}
          </strong>
          {productCode && (
            <small className='englishText' dir='ltr'>
              {productCode}
            </small>
          )}
        </td>
        <td className='mp-invoice-cell--number text-center' data-label='تعداد یا مقدار'>
          <b>{numberWithCommas(rw.qty)}</b>
          {rw.unit?.label && <small>{rw.unit.label}</small>}
          {variableD == 4 && (
            <div className='mp-invoice-return-quantity'>
              <label htmlFor={`return-quantity-${rw.id}`}>مقدار برگشت</label>
              <input
                id={`return-quantity-${rw.id}`}
                type='number'
                min={0}
                step='any'
                value={value}
                onChange={(event) => setValue(Number(event.currentTarget.value))}
                disabled={readOnly}
                className='form-control'
              />
              <small>باقی‌مانده: {numberWithCommas(remainingQuantity)}</small>
            </div>
          )}
        </td>
        <td className='mp-invoice-cell--money text-center' data-label='مبلغ واحد'>
          <b>{numberWithCommas(rw.amount)}</b>
          <small>ریال</small>
          {invoiceTemplate == 11 && (
            <small className='d-block text-gray-500' title='به سامانه ارسال نمی‌شود'>
              محاسباتی
            </small>
          )}
        </td>
        {showDiscount && (
          <td className='mp-invoice-cell--money text-center' data-label='تخفیف'>
            {variableD === 4 && (rw.discount || 0) > 0 ? (
              <div className='mp-invoice-return-discount'>
                <label htmlFor={`return-discount-${rw.id}`}>تخفیف برگشت</label>
                <input
                  id={`return-discount-${rw.id}`}
                  type='number'
                  min={0}
                  max={rw.discount}
                  step={1}
                  value={discount}
                  onChange={(event) => setDiscount(event.currentTarget.value)}
                  disabled={readOnly}
                  className='form-control'
                />
                <small>حداکثر: {numberWithCommas(rw.discount)} ریال</small>
              </div>
            ) : (
              <>
                <b>{numberWithCommas(variableD === 4 ? Number(discount) : rw.discount)}</b>
                <small>ریال</small>
              </>
            )}
            {!!rw.totalDiscount && (
              <small>سهم تخفیف کلی: {numberWithCommas(rw.totalDiscount)}</small>
            )}
          </td>
        )}
        {showTax && (
          <td className='mp-invoice-cell--tax text-center' data-label='مالیات و عوارض'>
            <b>{numberWithCommas(getVatPrice())}</b>
            <small>ارزش افزوده</small>
            {!!otherTaxPrice && <small>سایر: {numberWithCommas(otherTaxPrice)} ریال</small>}
          </td>
        )}
        <td className='mp-invoice-cell--final text-center' data-label='مبلغ نهایی'>
          <b>{numberWithCommas(getNetPrice())}</b>
          <small>ریال</small>
        </td>
        <td className='mp-invoice-cell--actions text-center' data-label='عملیات'>
          <div className='mp-invoice-row-actions'>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light-primary'
              title={expanded ? 'بستن جزئیات' : 'مشاهده جزئیات'}
              aria-label={expanded ? 'بستن جزئیات قلم' : 'مشاهده جزئیات قلم'}
              aria-expanded={expanded}
              onClick={() => setExpanded((current) => !current)}
            >
              <i
                className={`las ${expanded ? 'la-angle-up' : 'la-angle-down'}`}
                aria-hidden='true'
              ></i>
            </button>
            {!readOnly && variableD != 4 && (
              <>
                <button
                  type='button'
                  className='btn btn-icon btn-sm btn-light-info'
                  title='ویرایش قلم'
                  aria-label='ویرایش قلم'
                  onClick={() => setEditInvoiceItem(rw)}
                >
                  <i className='las la-edit' aria-hidden='true'></i>
                </button>
                <button
                  type='button'
                  className='btn btn-icon btn-sm btn-light-danger'
                  title='حذف قلم'
                  aria-label='حذف قلم'
                  onClick={() => deleteInvoiceItem(rw)}
                >
                  <i className='las la-trash-alt' aria-hidden='true'></i>
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className='mp-invoice-data-detail-row'>
          <td colSpan={columnCount}>
            <div className='mp-invoice-data-detail-row__content'>
              <div className='mp-invoice-data-detail-row__heading'>
                <strong>جزئیات قلم {ind + 1}</strong>
                <span>اطلاعات تکمیلی این ردیف بدون تغییر در اطلاعات ارسالی</span>
              </div>
              <InvoiceDetailsGrid
                items={[
                  {label: 'شناسه کالا یا خدمت', value: productCode, ltr: true},
                  {label: 'واحد اندازه‌گیری', value: rw.unit?.label},
                  {label: 'مبلغ قبل از تخفیف', value: `${numberWithCommas(getPrice())} ریال`},
                  {label: 'مبلغ تخفیف ردیف', value: `${numberWithCommas(rw.discount)} ریال`},
                  {
                    label: 'سهم از تخفیف کلی',
                    value: `${numberWithCommas(rw.totalDiscount)} ریال`,
                  },
                  {label: 'مبلغ پس از تخفیف', value: `${numberWithCommas(getAdPrice())} ریال`},
                  {label: 'نرخ مالیات بر ارزش افزوده', value: `${rw.vatRate || 0}٪`},
                  {
                    label: 'مبلغ مالیات بر ارزش افزوده',
                    value: `${numberWithCommas(getVatPrice())} ریال`,
                  },
                  {
                    label: rw.taxName || 'موضوع سایر مالیات و عوارض',
                    value: rw.taxRate ? `${rw.taxRate}٪` : undefined,
                  },
                  {
                    label: 'مبلغ سایر مالیات و عوارض',
                    value: rw.taxRate ? `${numberWithCommas(getTaxPrice())} ریال` : undefined,
                  },
                  {
                    label: rw.govTaxName || 'موضوع سایر وجوه قانونی',
                    value: rw.govTaxRate ? `${rw.govTaxRate}٪` : undefined,
                  },
                  {
                    label: 'مبلغ سایر وجوه قانونی',
                    value: rw.govTaxRate ? `${numberWithCommas(getGovTaxPrice())} ریال` : undefined,
                  },
                  {label: 'شماره قرارداد', value: rw.contractNo, ltr: true},
                  {label: 'عیار', value: rw.carat},
                  {
                    label: 'اجرت ساخت',
                    value: rw.wage != null ? `${numberWithCommas(rw.wage)} ریال` : undefined,
                  },
                  {
                    label: 'سود فروشنده',
                    value: rw.profit != null ? `${numberWithCommas(rw.profit)} ریال` : undefined,
                  },
                  {
                    label: 'حق‌العمل',
                    value:
                      rw.commission != null ? `${numberWithCommas(rw.commission)} ریال` : undefined,
                  },
                  {label: 'نوع ارز', value: rw.currency?.label},
                  {label: 'میزان ارز', value: rw.amountCurrency},
                  {label: 'ارزش ارزی', value: rw.currencyValue},
                  {
                    label: 'نرخ برابری ارز با ریال',
                    value: rw.currencyRate
                      ? `${numberWithCommas(rw.currencyRate)} ریال`
                      : undefined,
                  },
                  {
                    label: 'نرخ خرید ارز',
                    value: rw.currencyPurchaseRate
                      ? `${numberWithCommas(rw.currencyPurchaseRate)} ریال`
                      : undefined,
                  },
                  {
                    label: 'مأخذ مالیات بر ارزش افزوده',
                    value: rw.vatBaseAmount
                      ? `${numberWithCommas(rw.vatBaseAmount)} ریال`
                      : undefined,
                  },
                  {label: 'وزن خالص', value: rw.weight},
                ]}
              />
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  )
}

export {Row}
